import { FiAbstractStateComponent } from '../fi-state-proxy/fi-abstract-state.component';
import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { StateAccessCheckService } from '../fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '../types';
import { FiForumListContainerComponent } from '@fi-shared/fi-forum/fi-forum-list/fi-forum-list-container.component';
import { FiForumViewContainerComponent } from '@fi-shared/fi-forum/fi-forum-view/fi-forum-view-container.component';
import { FiForumSharedService } from '@fi-shared/fi-forum/fi-forum-shared.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { FiForumPostViewContainerComponent } from '@fi-shared/fi-forum/fi-forum-post-view/fi-forum-post-view-container.component';
import { FiForumPostAddContainerComponent } from '@fi-shared/fi-forum/fi-forum-post-edit/fi-forum-post-add-container.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IForumLightWeight } from '@fi-shared/fi-forum/fi-forum';

const ForumState: Ng2StateDeclaration = {
    name: FiStateNames.Forum._name,
    url: '/Forum',
    abstract: true,
    component: FiAbstractStateComponent,
};

const ForumListState: Ng2StateDeclaration = {
    name: FiStateNames.Forum.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    reloadOnSearch: false,
    views: {
        '@Module': {
            component: FiForumListContainerComponent,
        },
    },
};

const ForumViewState: Ng2StateDeclaration = {
    name: FiStateNames.Forum.View,
    url: '/{id:[0-9]+}/?:filterType&:sortColumn&:sortDirection&:offset',
    reloadOnSearch: false,
    views: {
        '@Module': {
            component: FiForumViewContainerComponent,
        },
    },
    resolve: {
        forum: [FiForumSharedService, FiStateService, Transition, forumView_forum],
    },
};
export function forumView_forum(
    forumService: FiForumSharedService,
    stateService: FiStateService,
    transition: Transition,
) {
    const forumId = coerceNumberProperty(stateService.getParams(transition).id);
    return forumService.getForumForView(forumId);
}

const ForumPostState: Ng2StateDeclaration = {
    name: FiStateNames.Forum.Post._name,
    url: '/{forumId:[0-9]+}/Post',
    abstract: true,
    component: FiAbstractStateComponent,
    resolve: {
        forumId: [FiStateService, Transition, forumPost_forumId],
    },
};
export function forumPost_forumId(fiStateService: FiStateService, transition: Transition) {
    const forumId = coerceNumberProperty(fiStateService.getParams(transition).forumId);
    return forumId;
}

const ForumPostViewState: Ng2StateDeclaration = {
    name: FiStateNames.Forum.Post.View,
    url: '/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiForumPostViewContainerComponent,
        },
    },
    resolve: {
        forumPost: [FiForumSharedService, FiStateService, Transition, forumPostView_forumPost],
        comments: [FiStateService, Transition, FiCommentsService, forumPostView_comments],
        commentId: [FiStateService, Transition, forumPostView_commentId],
        isEditMode: [FiStateService, Transition, forumPostView_isEditMode],
    },
    params: {
        commentId: null,
        isEditMode: false,
    },
};
export function forumPostView_forumPost(
    forumService: FiForumSharedService,
    fiStateService: FiStateService,
    transition: Transition,
) {
    const forumPostId = coerceNumberProperty(fiStateService.getParams(transition).id);
    return forumService.getForumPost(forumPostId);
}

export function forumPostView_commentId(fiStateService: FiStateService, transition: Transition) {
    return coerceNumberProperty(fiStateService.getParams(transition).commentId);
}

export function forumPostView_isEditMode(fiStateService: FiStateService, transition: Transition) {
    return coerceBooleanProperty(fiStateService.getParams(transition).isEditMode);
}

export function forumPostView_comments(
    fiStateService: FiStateService,
    transition: Transition,
    commentsService: FiCommentsService,
) {
    const forumPostId = coerceNumberProperty(fiStateService.getParams(transition).id);
    return commentsService.getComments(forumPostId, ObjectTypes.ForumPost);
}

const ForumPostAddState: Ng2StateDeclaration = {
    name: FiStateNames.Forum.Post.Add,
    url: '/Add',
    views: {
        '@Module': {
            component: FiForumPostAddContainerComponent,
        },
    },
    resolve: {
        forumId: [FiStateService, Transition, forumPostAdd_forumId],
        availableForums: [FiForumSharedService, forumPostAdd_availableForums],
    },
};
export function forumPostAdd_forumId(fiStateService: FiStateService, transition: Transition) {
    const forumId = coerceNumberProperty(fiStateService.getParams(transition).forumId);
    return forumId;
}

export function forumPostAdd_availableForums(fiForumSharedService: FiForumSharedService) {
    return fiForumSharedService.getForumsLightWeight();
}

export const NewForumStates: StatesModule = {
    states: [ForumState, ForumListState, ForumViewState, ForumPostState, ForumPostViewState, ForumPostAddState],
};

export function registerNewForumStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules([FiStateNames.Forum.Edit, FiStateNames.Forum.Add], {
        SettingType: [ApplicationSettingsType.ForumAdmin],
        AccessType: AccessRightType.Read,
    });
}
